import { useMemo } from 'react';
import { PortalDsBadge } from 'src/components/portal-design-system';
import { DataTable, type DataTableColumn } from 'src/components/shared';
import {
  formatConnectedDate,
  formatSchedulingConnectionStatusLabel,
  getSchedulingConnectionBadgeVariant,
} from '../utils/scheduling.mappers';
import type { SchedulingConnection } from '../types/scheduling.types';

interface ConnectionsTableProps {
  connections: SchedulingConnection[];
  onSelectConnection: (connection: SchedulingConnection) => void;
  disabled?: boolean;
}

export function ConnectionsTable({
  connections,
  onSelectConnection,
  disabled = false,
}: ConnectionsTableProps) {
  const columns = useMemo<DataTableColumn<SchedulingConnection>[]>(() => [
    {
      id: 'name',
      header: 'Name',
      accessor: (connection) => connection.name,
      render: (connection) => (
        <button
          type="button"
          disabled={disabled}
          onClick={() => onSelectConnection(connection)}
          className="text-left font-medium text-[#0D0F1A] transition-colors hover:text-[#2563EB] disabled:cursor-not-allowed disabled:opacity-50"
        >
          {connection.name}
        </button>
      ),
    },
    {
      id: 'email',
      header: 'Email',
      accessor: (connection) => connection.email,
      render: (connection) => (
        <span className="text-[#5E6278]">
          {connection.email}
        </span>
      ),
    },
    {
      id: 'calendars',
      header: 'Included calendars',
      accessor: (connection) => connection.calendars.filter((calendar) => calendar.is_included).length,
      render: (connection) => {
        const includedCount = connection.calendars.filter((calendar) => calendar.is_included).length;
        return (
          <span className="text-[#5E6278]">
            {includedCount}
            {' '}
            of
            {' '}
            {connection.calendars.length}
          </span>
        );
      },
    },
    {
      id: 'status',
      header: 'Status',
      accessor: (connection) => connection.status,
      render: (connection) => (
        <PortalDsBadge
          variant={getSchedulingConnectionBadgeVariant(connection.status)}
          pill
        >
          {formatSchedulingConnectionStatusLabel(connection.status)}
        </PortalDsBadge>
      ),
    },
    {
      id: 'connectedAt',
      header: 'Connected',
      accessor: (connection) => connection.connected_at ?? '',
      render: (connection) => (
        <span className="text-[#5E6278]">
          {formatConnectedDate(connection.connected_at)}
        </span>
      ),
    },
    {
      id: 'actions',
      header: 'Actions',
      accessor: () => '',
      render: (connection) => (
        <button
          type="button"
          disabled={disabled}
          onClick={() => onSelectConnection(connection)}
          className="text-[13px] font-semibold text-[#2563EB] transition-colors hover:text-[#1D4ED8] disabled:cursor-not-allowed disabled:opacity-50"
        >
          Edit
        </button>
      ),
    },
  ], [disabled, onSelectConnection]);

  return (
    <DataTable
      rows={connections}
      columns={columns}
      pageSize={25}
      getRowKey={(connection) => connection.id}
      emptyMessage="No calendars connected yet. Add your calendar to join the booking pool."
    />
  );
}
